import {
    CustomerType,
    DiscountType,
    PaymentStatus,
    PaymentType,
} from '@repo/common';
import { z } from 'zod';

import { PaginationResponseSchema } from '@/shared/global.schema';

const CustomerSchema = z.object({
    id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    created_by: z.string(),
    name: z.string(),
    address: z.string(),
    phone_number: z.string(),
    email: z.string().email(),
    customer_type: z.nativeEnum(CustomerType),
    serial_id: z.coerce.string().optional(),
});

const TransactionSchema = z.object({
    id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    user_id: z.string(),
    payment_id: z.string(),
    amount: z.number(),
    transaction_type: z.nativeEnum(PaymentType),
    note: z.string().optional().nullable(),

    payment_method: z.string().optional().nullable(),
    transaction_id: z.string().optional().nullable(),
});
const PaymentSchema = z.object({
    id: z.string(),
    total_price: z.number(),
    type: z.nativeEnum(PaymentType),
    status: z.nativeEnum(PaymentStatus),
    total_paid: z.number().nullable(),
    transactions: z.array(TransactionSchema).nullable(),
    payment_method: z.string().nullable(),
});
const OrderItemSchema = z.object({
    id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    user_id: z.string(),
    order_id: z.string(),
    batch_id: z.string(),
    discount_type: z.string().nullable(),
    discount_value: z.number().nullable(),
    quantity: z.number().min(0),
    price: z.number().min(0),
    product_name: z.string(),
});
const userSchema = z.object({
    id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('Invalid email address'),
    phone_number: z
        .string()
        .regex(/^\d{11}$/, 'Phone number must be 11 digits'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    role_id: z.string(),
});
export const ResponseSingleOrderSchema = z.object({
    data: z.object({
        id: z.string(),
        created_at: z.string().datetime(),
        updated_at: z.string().datetime(),
        user_id: z.string(),
        customer_id: z.string().nullable(),
        payment_id: z.string(),
        discount_type: z.nativeEnum(DiscountType).nullable(),
        discount_value: z.number().min(0),
        tax: z.number().min(0),
        note: z.string().nullable(),
        status: z.nativeEnum(PaymentStatus),

        customer_type: z.nativeEnum(CustomerType),
        invoice_number: z.string(),
        customer: CustomerSchema.nullable(),
        payment: PaymentSchema.nullable(),
        items: z.array(OrderItemSchema),
        user: userSchema.nullable(),
    }),
});

export type SingleOrder = z.infer<typeof ResponseSingleOrderSchema>['data'];

export const ResponseOrderSchema = z.object({
    data: z.array(
        z.object({
            id: z.string(),
            user_id: z.string(),
            customer_id: z.string().nullable(),
            payment_id: z.string(),
            discount_type: z.string().nullable(),
            created_at: z.string().datetime(),
            updated_at: z.string().datetime(),
            discount_value: z.number(),
            tax: z.number(),
            note: z.string().nullable(),
            customer_type: z.nativeEnum(CustomerType),
            invoice_number: z.string().nullable(),
            status: z.nativeEnum(PaymentStatus),
            customer: CustomerSchema.nullable(),
            payment: PaymentSchema.nullable(),
            total_paid: z.number().nullable().optional(),
        }),
    ),
    pagination: PaginationResponseSchema,
});

export type OrderSchema = z.infer<typeof ResponseOrderSchema>;
export type SingleOrderSchema = z.infer<typeof ResponseSingleOrderSchema>;
export type SingleOrderItem = z.infer<typeof OrderItemSchema>;

const OrderListItemSchema = z.object({
    id: z.string(),
    invoice_number: z.string().nullable(),
    customer_id: z.string().nullable(),
    created_at: z.string(),
    user_id: z.string(),
    discount_type: z.string().nullable(),
    discount_value: z.number(),
    order_type: z.string(),
    note: z.string().nullable(),
    tax: z.number(),
    customer: z
        .object({
            name: z.string().nullable(),
            customer_type: z.string().nullable(),
            id: z.string().nullable(),
        })
        .nullable(),
    payment: z.object({
        total_price: z.number(),
        status: z.nativeEnum(PaymentStatus),
        total_paid: z.number().nullable(),
        total_due: z.number().nullable(),
        estimated_profit_margin: z.number().nullable(),
    }),
});

export const ResponseOrderListSchema = z.object({
    data: z.array(OrderListItemSchema),
    pagination: PaginationResponseSchema,
});

export type OrderListItemSchemaType = z.infer<typeof OrderListItemSchema>;
export type ResponseOrderListSchemaType = z.infer<
    typeof ResponseOrderListSchema
>;
